import { TapUser, TapStatus } from '@/domain/types';
import { TapSessionState, TapSessionActions } from '@/hooks/useTapSession';
import { COIN_SIZE, BONUS_ARC_SIZE, TAP_LEVEL3_THRESHOLD } from '@/domain/constants';
import { HUD } from './HUD';
import { BonusArc } from './BonusArc';
import { TapCoin } from './TapCoin';
import { useState, useCallback, useRef, useEffect } from 'react';

interface TapScreenProps {
  user: TapUser | null;
  status: TapStatus | null;
  sessionState: TapSessionState;
  sessionActions: TapSessionActions;
  onRefreshStatus: () => Promise<void>;
}

export function TapScreen({
  user,
  status,
  sessionState,
  sessionActions,
}: TapScreenProps) {
  const [tapDelta, setTapDelta] = useState<number>(0);
  // Floating feedback elements from coin center (allow a few to fly simultaneously)
  const [floatingFeedbacks, setFloatingFeedbacks] = useState<{ id: number; value: number }[]>([]);
  // Track if user has tapped (for helper text)
  const [hasTapped, setHasTapped] = useState(false);
  // Track recent tap timestamps for instant TPS calculation
  const recentTapsRef = useRef<number[]>([]);
  const [instantTPS, setInstantTPS] = useState(0);

  // Decay instant TPS when not tapping
  useEffect(() => {
    if (!sessionState.isActive) {
      recentTapsRef.current = [];
      setInstantTPS(0);
      return;
    }
    const interval = setInterval(() => {
      const now = Date.now();
      recentTapsRef.current = recentTapsRef.current.filter(t => now - t < 300);
      const next = Math.round(recentTapsRef.current.length / 0.3);
      setInstantTPS((prev) => (prev === next ? prev : next));
    }, 150);
    return () => clearInterval(interval);
  }, [sessionState.isActive]);

  const handleTap = useCallback(
    () => {
      if (!sessionState.isActive) return;

      // Mark that user has tapped (hides helper text)
      if (!hasTapped) setHasTapped(true);

      // Register tap
      sessionActions.registerTap();

      // Track tap for instant TPS (300ms window)
      const now = Date.now();
      recentTapsRef.current.push(now);
      // Keep only taps in last 300ms
      recentTapsRef.current = recentTapsRef.current.filter(t => now - t < 300);
      // Calculate instant TPS
      const tapsInWindow = recentTapsRef.current.length;
      setInstantTPS(Math.round(tapsInWindow / 0.3));

      // Haptic feedback
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');

      // Calculate points for this tap based on current level
      const levelPoints = sessionState.currentLevel === 3 ? 3 : sessionState.currentLevel === 2 ? 2 : 1;
      setTapDelta((prev) => prev + levelPoints);

      // Add floating feedback (keep last 20)
      setFloatingFeedbacks((prev) => [
        ...prev.slice(-19),
        { id: Date.now(), value: levelPoints },
      ]);

      // Clear delta display after batch
      setTimeout(() => setTapDelta(0), 100);
    },
    [sessionState.isActive, sessionState.currentLevel, sessionActions, hasTapped]
  );

  const handleStartSession = async () => {
    await sessionActions.startSession();
  };

  const sessionsRemaining = status?.sessionsRemaining ?? user?.sessionsRemaining ?? 0;
  const canStart = sessionsRemaining > 0 && !sessionState.isActive && !sessionState.isLoading;

  // TapCoin now includes circuit rings, making it larger
  const coinAreaSize = COIN_SIZE + 60;
  const totalAreaSize = Math.max(coinAreaSize, BONUS_ARC_SIZE);

  // Calculate arc progress based on tap rate (TPS)
  // Use actual elapsed time from session start, not from duration - timeRemaining
  const elapsedSeconds = sessionState.startTime
    ? (Date.now() - sessionState.startTime.getTime()) / 1000
    : 0;
  const avgTPS = elapsedSeconds > 1 ? sessionState.acceptedTaps / elapsedSeconds : 0;
  const arcProgress = Math.min(1, instantTPS / TAP_LEVEL3_THRESHOLD);

  return (
    <div className="cosmic-bg relative flex h-full flex-col items-center justify-center overflow-hidden">
      {/* HUD */}
      <div className="absolute left-4 right-4 top-4 z-10">
        <HUD
          endTime={sessionState.endTime}
          points={(user?.points ?? 0) + sessionState.estimatedPoints}
          tapDelta={tapDelta}
          isActive={sessionState.isActive}
          currentLevel={sessionState.currentLevel}
        />
      </div>

      {/* Center coin area */}
      <div
        className="relative flex items-center justify-center"
        style={{ width: totalAreaSize + 40, height: totalAreaSize + 40, overflow: 'visible' }}
      >
        {/* Bonus arc behind coin */}
        <BonusArc
          level={sessionState.currentLevel}
          isActive={sessionState.isActive}
          canPlay={canStart}
          bonusTime={sessionState.bonusTimeAwarded}
          progress={arcProgress}
        />

        {/* Tap coin with circuit rings */}
        <TapCoin isActive={sessionState.isActive} canPlay={canStart} onTap={handleTap} />

        {/* Floating feedback from coin center */}
        {floatingFeedbacks.map((fb) => (
          <div
            key={fb.id}
            className="pointer-events-none absolute left-1/2 top-1/2 z-50 text-2xl font-bold text-glow-cyan glow-cyan floating-feedback"
            onAnimationEnd={() => setFloatingFeedbacks((prev) => prev.filter((f) => f.id !== fb.id))}
          >
            +{fb.value}
          </div>
        ))}
      </div>

      {/* Helper text below coin - absolute to avoid layout shift */}
      {sessionState.isActive && !hasTapped && (
        <p className="absolute left-0 right-0 text-center animate-pulse text-sm text-gray-400"
          style={{ top: `calc(50% + ${totalAreaSize / 2 + 16}px)` }}
        >
          TAP TO SCORE
        </p>
      )}

      {/* Session info / Start button */}
      <div className="absolute bottom-24 left-0 right-0 z-10 flex flex-col items-center gap-4">
        {!sessionState.isActive && (
          <>
            <p className="text-sm text-gray-400">
              {sessionsRemaining} session{sessionsRemaining !== 1 ? 's' : ''} remaining today
            </p>
            <button
              onClick={handleStartSession}
              disabled={!canStart}
              className={`px-14 py-4 text-lg font-bold uppercase tracking-widest transition-all duration-300 ${
                canStart
                  ? 'text-white hover:shadow-[0_0_30px_rgba(0,162,255,0.3)] active:scale-[0.98]'
                  : 'text-gray-500'
              }`}
              style={{
                background: canStart ? 'rgba(0, 162, 255, 0.15)' : 'rgba(100, 100, 100, 0.1)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: canStart ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid rgba(100, 100, 100, 0.2)',
                borderRadius: '4px',
              }}
            >
              {sessionState.isLoading ? 'STARTING...' : 'START GAME'}
            </button>
          </>
        )}

        {sessionState.isActive && (
          <p className="text-lg font-bold text-glow-cyan glow-cyan-sm">
            L{sessionState.currentLevel} • {sessionState.acceptedTaps} taps • avg:{avgTPS.toFixed(0)} inst:{instantTPS}
          </p>
        )}
      </div>

      {/* Error message */}
      {sessionState.error && (
        <div className="absolute bottom-4 left-4 right-4 z-10 rounded-lg bg-red-900/50 p-3 text-center text-sm text-red-300"
          style={{ border: '1px solid rgba(255, 100, 100, 0.3)' }}
        >
          {sessionState.error}
        </div>
      )}
    </div>
  );
}
